import React  from 'react'
import { View, StyleSheet } from 'react-native'
import { Icon, Text, useTheme } from '@ui-kitten/components'
import { FontAwesome5 } from '@expo/vector-icons'
import { useTranslation } from 'react-i18next'
import { currencyFormatterAtom, storesDictAtom, suppliersDictAtom, usersDictAtom } from '../../global/AtomsProvider'
import { useAtomValue } from 'jotai'
import OperationsList from '../../components/OperationsList'
import { cartItemNames } from '../SalesHistory/SalesHistoryScreen'

/**
 * Find a user by any ID format (Supabase UUID or Firestore ID)
 */
const findUserByAnyId = (usersDict, ...ids) => {
  for (const id of ids) {
    if (id && usersDict[id]) return usersDict[id]
  }
  const userIds = ids.filter(id => id)
  if (userIds.length > 0) {
    for (const user of Object.values(usersDict)) {
      if (userIds.includes(user.id) || userIds.includes(user.firestoreId)) return user
    }
  }
  return null
}

/**
 * Find a store by any ID format (Supabase UUID or Firestore ID)
 */
const findStoreByAnyId = (storesDict, ...ids) => {
  for (const id of ids) {
    if (id && storesDict[id]) return storesDict[id]
  }
  const storeIds = ids.filter(id => id)
  if (storeIds.length > 0) {
    for (const store of Object.values(storesDict)) {
      if (storeIds.includes(store.id) || storeIds.includes(store.firestoreId)) return store
    }
  }
  return null
}

export default function SuppliesScreen({ navigation, supplierID=null, initialRange=null, showTotal=false }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const usersDict = useAtomValue(usersDictAtom)
  const suppliersDict = useAtomValue(suppliersDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const storesDict = useAtomValue(storesDictAtom)

  const renderItemFieldsToShow = (item, date) => {
    const supplier = suppliersDict[item.supplierID]
    return (
      <>
        <View style={styles.dateTimeView}>
          <Icon name='car-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {supplier?.name || t('supplies.deletedSupplier')}
          </Text>
        </View>

        <View style={styles.dateTimeView}>
          <Icon name='file-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {currencyFormatter.format(item.totalSum)} 
            {/* + ' (' + item.items?.reduce((total, currentItem) => total + currentItem?.quantity, 0) +  ')'} */}
          </Text>
        </View>
        
        <View style={styles.dateTimeView}>
          <Icon name='clock-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {date}
          </Text>
        </View>

        <View style={styles.dateTimeView}>
          <Icon name='credit-card-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category={supplier?.paidPeriodically ? 's2' : 's2'}
            status={item.isPaid ? 'basic' : 'danger'}>
            {item.isPaid ? t('supplies.paid') : t('supplies.notPaid')} {item.isPaid ? item.paymentMethod?.text : ''}
          </Text>
        </View>

        <View style={styles.dateTimeView}>
          <Icon name='person-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {findUserByAnyId(usersDict, item.cashierUID, item.cashier_id, item.cashierId)?.name}
          </Text>
        </View>

        <View style={styles.dateTimeView}>
          <FontAwesome5 name="building" size={16} color={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {findStoreByAnyId(storesDict, item.store, item.store_id, item.storeId)?.name}
          </Text>
        </View>

        <Text style={{marginTop: 2}}>
          {cartItemNames(item.items)}
        </Text>
        
      </>
    )
  }

  /**
   * ATL-986: declared for BOTH read paths. Without the `api` half, the supplier tab
   * on SupplierDetailScreen listed EVERY supplier's supplies and totalled them.
   */
  const additionalFilters = {
    firestore: (unsubscribe) => {
      if (supplierID != null) {
        unsubscribe = unsubscribe.where('supplierID', '==', supplierID)
      }
      return unsubscribe
    },
    api: supplierID != null ? { supplierId: supplierID } : {},
  }

  return (
    <View style={styles.container}>      
      <OperationsList collectionName='supplies' dateField={'supplyDate'} additionalFilters={additionalFilters} 
        detailScreenName={'SupplyDetail'} topView={() => {return null}} initialRange={initialRange}
        showTotalSum={showTotal} totalSumFormatter={currencyFormatter} totalSumField={'totalSum'}
        showAddButton={showTotal ? false : true} additionalDataValidation={() => {}} detailScreenParams={{}}
        renderItemFieldsToShow={renderItemFieldsToShow} navigation={navigation} showStoreFilter={true}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  icon: {
    marginRight: 4,
  },
  itemTitle: {
    marginRight: 25,
  },
  dateTimeView: {
    flexDirection: 'row',
    alignItems: 'center'
  },
});
